import { useRouter } from 'next/router';

import { FindPharmacyDdAction } from '@/constants/dd-actions/pharmacy';
import useNonBenefitMapNearByPharmacySearchTabs from '@/hooks/non-benefit/useNonBenefitMapNearbyPharmacySearchTabs';
import { NearbyPharmacyFilterModel } from '@/models/pharmacy';
import { DiagnosisType } from '@/types/bff/gql-schema';
import {
  DispensationUnitVo,
  ENearbyPharmacySearchFilterType,
  NearbyPharmacyMedicineVo,
  NearbyPharmacySearchTabsVo,
  NearbyPharmacySortsVo,
  PharmacySearchQueries,
  TreatmentMedicineStrengthVo,
} from '@/types/pharmacy';
import { updateSearchParams } from '@/utils/url';
import { useMemo } from 'react';
import useLogger from '../useLogger';

interface Tab {
  id: string;
  name: string;
  diagnosisType?: DiagnosisType | null;
  diagnosisTypeId?: string | null;
  sortTypes: Array<NearbyPharmacySortsVo>;
  filters: NearbyPharmacyFilterModel[];
  treatmentMedicines: NearbyPharmacyMedicineVo[];
}

const useNonBenefitMapCategoryTabs = (): {
  tabs?: NearbyPharmacySearchTabsVo[];
  selectedTab: Tab;
  selectTab: (tab: Tab) => void;
  shouldIncludeDiagnosisPrice: boolean;
  selectedTreatmentMedicine: NearbyPharmacyMedicineVo;
  selectedTreatmentMedicineUnit: DispensationUnitVo;
  selectedTreatmentMedicineStrength: TreatmentMedicineStrengthVo | null;
} => {
  const router = useRouter();
  const {
    diagnosisTypeId = '',
    treatmentMedicineId = '',
    treatmentMedicineUnitId = '',
    treatmentMedicineStrengthId = '',
  } = router.query as PharmacySearchQueries;

  const { data: tabs } = useNonBenefitMapNearByPharmacySearchTabs();

  const selectedTab = useMemo(() => {
    return tabs.find((tab) => tab.diagnosisTypeId === diagnosisTypeId) ?? tabs[0];
  }, [tabs, diagnosisTypeId]);

  const selectedTreatmentMedicine = useMemo(() => {
    return (
      selectedTab.treatmentMedicines.find((medicine) => medicine.treatmentMedicineId === treatmentMedicineId) ??
      selectedTab.treatmentMedicines[0]
    );
  }, [selectedTab, treatmentMedicineId]);

  const selectedTreatmentMedicineUnit = useMemo(() => {
    return (
      selectedTreatmentMedicine.dispensationUnits.findLast(
        (unit) => unit.treatmentMedicineUnitId === Number(treatmentMedicineUnitId)
      ) ??
      selectedTreatmentMedicine.dispensationUnits.find((unit) => unit.isDefault) ??
      selectedTreatmentMedicine.dispensationUnits[0]
    );
  }, [selectedTreatmentMedicine, treatmentMedicineUnitId]);

  const selectedTreatmentMedicineStrength = useMemo(() => {
    if (!selectedTreatmentMedicine.treatmentMedicineStrengths.length) return null;

    return (
      selectedTreatmentMedicine.treatmentMedicineStrengths.find(
        (strength) => strength.treatmentMedicineStrengthId === treatmentMedicineStrengthId
      ) ?? selectedTreatmentMedicine.treatmentMedicineStrengths[0]
    );
  }, [selectedTreatmentMedicine, treatmentMedicineStrengthId]);

  const shouldIncludeDiagnosisPrice = selectedTab.filters.some(
    (filter) => filter.type === ENearbyPharmacySearchFilterType.IncludeDiagnosisPrice
  );

  const { log } = useLogger();

  const selectTab = (tab: Tab) => {
    const defaultTreatmentMedicine = tab.treatmentMedicines[0];

    const defaultDispensationUnit =
      defaultTreatmentMedicine.dispensationUnits.find((unit) => unit.isDefault) ||
      defaultTreatmentMedicine.dispensationUnits[0];

    const defaultTreatmentMedicineStrength =
      defaultTreatmentMedicine.treatmentMedicineStrengths.length > 0
        ? defaultTreatmentMedicine.treatmentMedicineStrengths.find((strength) => strength.isDefault) ||
          defaultTreatmentMedicine.treatmentMedicineStrengths[0]
        : null;

    updateSearchParams([
      {
        key: 'diagnosisType',
        value: tab.diagnosisType ?? '',
      },
      {
        key: 'diagnosisTypeId',
        value: tab.diagnosisTypeId ?? '',
      },
      { key: 'treatmentMedicineId', value: defaultTreatmentMedicine.treatmentMedicineId ?? '' },
      { key: 'treatmentMedicineUnitId', value: defaultDispensationUnit.treatmentMedicineUnitId ?? '' },
      ...(defaultTreatmentMedicineStrength
        ? [
            {
              key: 'treatmentMedicineStrengthId',
              value: defaultTreatmentMedicineStrength.treatmentMedicineStrengthId ?? '',
            },
          ]
        : [{ key: 'treatmentMedicineStrengthId', value: '' }]),
    ]);

    log({
      eventName: `${FindPharmacyDdAction.ClickTab}-${tab.id}`,
    });
  };

  return {
    tabs,
    selectedTab,
    selectTab,
    selectedTreatmentMedicine,
    selectedTreatmentMedicineUnit,
    selectedTreatmentMedicineStrength,
    shouldIncludeDiagnosisPrice,
  };
};

export default useNonBenefitMapCategoryTabs;
